import { Howl } from 'howler';
import type { HowlOptions } from 'howler';
import type { AudioTrack } from './types';

/**
 * `xhrWithCredentials` was replaced by `xhr: { withCredentials }` in howler 2.2 and is ignored
 * by the version this package depends on. Kept for now so behaviour is unchanged.
 */
type LegacyHowlOptions = HowlOptions & { xhrWithCredentials?: boolean };

/** howler exposes the ids of the sounds it has created only on this internal field. */
type HowlWithSounds = Howl & { _sounds: Array<{ _id: number }> };

export default class AudioHandler {
  declare audioTracks: Record<string, Howl>;

  declare activeAudioTrack: string;

  declare trackName: string;

  declare onVolumeChangeCallback?: (volume: number) => void;

  constructor(onVolumeChangeCallback?: (volume: number) => void) {
    this.audioTracks = {};
    this.activeAudioTrack = '';
    this.trackName = '';
    this.onVolumeChangeCallback = onVolumeChangeCallback;
  }

  addAudioTracks(tracks: AudioTrack[]) {
    tracks.forEach((track) => {
      if (track.narratorPath) {
        const sources = [track.narratorPath];
        if (track.oggNarratorPath) {
          sources.push(track.oggNarratorPath);
        }
        this.audioTracks[track.langCode] = new Howl({
          src: sources,
          xhrWithCredentials: false,
          html5: true,
          preload: false,
        } as LegacyHowlOptions);
      }
    });
  }

  setTrackName(trackName: string) {
    this.trackName = trackName;
  }

  setActiveAudioTrack(lang: string) {
    this.activeAudioTrack = lang;
  }

  seek(playTime?: number) {
    if (typeof this.audioTracks[this.activeAudioTrack] !== 'undefined') {
      if (this.audioTracks[this.activeAudioTrack].state() !== 'loaded') {
        this.audioTracks[this.activeAudioTrack].load();
      }
      this.audioTracks[this.activeAudioTrack].seek(playTime || 0);
    }
  }

  play() {
    if (typeof this.audioTracks[this.activeAudioTrack] !== 'undefined') {
      if (this.audioTracks[this.activeAudioTrack].state() !== 'loaded') {
        this.audioTracks[this.activeAudioTrack].load();
      }
      this.audioTracks[this.activeAudioTrack].play(
        (this.audioTracks[this.activeAudioTrack] as HowlWithSounds)._sounds[0]._id,
      );
    }
  }

  pause() {
    if (typeof this.audioTracks[this.activeAudioTrack] !== 'undefined') {
      this.audioTracks[this.activeAudioTrack].pause();
    }
  }

  destroy() {
    Object.keys(this.audioTracks).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(this.audioTracks, key)) {
        this.audioTracks[key].unload();
      }
    });
    this.audioTracks = {};
  }

  setVolume(value: number) {
    Object.keys(this.audioTracks).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(this.audioTracks, key)) {
        this.audioTracks[key].volume(value);
      }
    });
    if (this.onVolumeChangeCallback) {
      this.onVolumeChangeCallback(value);
    }
  }

  setMute(value: boolean) {
    Object.keys(this.audioTracks).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(this.audioTracks, key)) {
        this.audioTracks[key].mute(value);
      }
    });
    if (this.onVolumeChangeCallback) {
      this.onVolumeChangeCallback(0);
    }
  }

  setPlaybackRate(rate: number) {
    if (typeof this.audioTracks[this.activeAudioTrack] !== 'undefined') {
      this.audioTracks[this.activeAudioTrack].rate(rate);
    }
  }
}
